import {
	CreateCampaignObjective,
	CreateCreativeContentCallToAction,
	CreateCreativeType,
} from 'wb-reddit/graphql/generated';
import {
	boolean, object, string, array, mixed,
} from 'yup';

const mediaUrlContentValidator = object({
	mediaUrl: string().url('Media url is not a valid url'),
}).default(undefined).required('Media is required');

const callToActionValidator = string().required('Call to action is required')
	.oneOf(Object.values(CreateCreativeContentCallToAction), 'Incorrect call to action');

const thumbnailValidator = string().notRequired().url();

const destinationUrlValidator = (campaignObjective?: CreateCampaignObjective) => string().url('Destination url must be a valid url')
	.when([], {
		is: () => campaignObjective === CreateCampaignObjective.AppInstalls,
		then(schema) {
			return schema.notRequired();
		},
		otherwise(schema) {
			return schema.required('Destination url is required');
		},
	});

const displayUrlValidator = string().notRequired().url('Display url must be a valid url');

const captionValidator = string().notRequired()
	.max(50, 'The caption should not exceed 50 characters.');

const eventValidator = (event: 'CLICK' | 'VIEW') => array().nullable().of(object().shape({
	url:  string().url('Invalid URL').required('Url is required'),
	type: string().test('isTheCorrectType', 'Incorrect event tracker type', (type) => type === event),
}));

function getCreativeValidations(data: Record<string, any>) {
	const filledMediaUrlsCount = Object.keys(data)
		.filter(key => key.includes('creativeContentMediaUrl') && data[key]).length;

	const mandatoryCreativeContentCount = data.creativeType === CreateCreativeType.Carousel
		? Math.max(filledMediaUrlsCount, 2)
		: 1;

	let creativeContentValidators = {};

	switch (data.creativeType) {
		case CreateCreativeType.Carousel:
		case CreateCreativeType.Image:
		case CreateCreativeType.Video:
			for (let i = 0; i < mandatoryCreativeContentCount; i++) {
				creativeContentValidators = {
					...creativeContentValidators,
					[`creativeContentMediaUrl${i}`]:     mediaUrlContentValidator,
					[`creativeContentCallToAction${i}`]: callToActionValidator,
					[`creativeContentDestinationUrl${i}`]:
						destinationUrlValidator(data?.parent?.parent?.campaignObjective),
					[`creativeContentDisplayUrl${i}`]: displayUrlValidator,
					[`creativeContentCaption${i}`]:    captionValidator,
					creativeText:                      mixed().nullable().oneOf(
						[null, undefined, ''],
						'Content is not allowed for this creative type',
					),
				};
			}

			break;
		case CreateCreativeType.Text:
		default:
			creativeContentValidators = {
				creativeContentCallToAction0:   callToActionValidator,
				creativeContentDestinationUrl0: destinationUrlValidator(data?.parent?.parent?.campaignObjective),
				creativeContentDisplayUrl0:     displayUrlValidator,
				creativeText:                   string()
					.required('Content is required')
					.max(40000, 'Content mustn\'t be longer than 100 characters'),
			};

			break;
	}

	return creativeContentValidators;
}

export const validateRedditAd = (data: Record<string, any>) => {
	const createValidations = {
		...(getCreativeValidations(data)),
		creativeType: string().required('Post type is required')
			.oneOf(Object.values(CreateCreativeType), 'Wrong Creative type'),
		creativeHeadline: string().required('Creative headline is required')
			.max(300, 'Post headline must not be longer than 300 characters').test({
				name:    'no-whitespace',
				test:    (value) => !(value.trim() === ''),
				message: "Post headline can't be only whitespace",
			}),
		creativeThumbnailUrl: data.creativeType === CreateCreativeType.Video ? thumbnailValidator
			.required('A thumbnail is required for video post type') : thumbnailValidator,
		creativeAllowComments: boolean().notRequired(),
		adClickEventTrackers:  eventValidator('CLICK'),
		adViewEventTrackers:   eventValidator('VIEW'),
	};

	return object().shape({
		adName: string().min(3, 'Name must be at least 3 characters')
			.max(500, 'Name must not be longer than 500 characters'),
		...(data.isInCreation ? createValidations : {}),
	}).validate(data, { abortEarly: false });
};
